import React from 'react';
import {
  ProductCarousel,
  ProductCarouselNavigationControl,
  ProductCarouselProduct,
} from '@noths/polaris-client-ribbons-design-system';
import { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { useRichRelevanceData } from '../../../hooks/useRichRelevanceData/useRichRelevanceData';
import { RecommendationsServiceProductData } from '../../../services/recommendations';
import {
  RichRelevanceProduct,
  RRDecoratedRecommendationsServiceProductData,
} from '../../../types/RichRelevance';

interface RecentlyViewedNavigateCarouselPayload {
  numberOfItems: number;
  strategy: string;
}

interface RecentlyViewedOnVisiblePayload {
  products: RRDecoratedRecommendationsServiceProductData[];
  strategy: string;
}

interface RecentlyViewedOnProductClickPayload {
  index: number;
  product: RRDecoratedRecommendationsServiceProductData;
  numberOfItems: number;
  strategy: string;
}

export interface RecentlyViewedProps {
  currency: CurrencyCode;
  items?: RichRelevanceProduct[];
  onNavigateCarousel?: (
    swiped: boolean,
    control: ProductCarouselNavigationControl,
    payload: RecentlyViewedNavigateCarouselPayload,
  ) => void;
  onVisible?: (payload: RecentlyViewedOnVisiblePayload) => void;
  onProductClick?: (
    e: React.MouseEvent<HTMLAnchorElement>,
    payload: RecentlyViewedOnProductClickPayload,
  ) => void;
  placementTitle?: string;
  strategy?: string;
}

const transformRecommendationsServiceProductDataToProductCarouselProduct = (
  {
    title,
    images,
    links,
    purchasable: isPurchasable,
    new: isNew,
    on_sale: onSale,
    prices,
    sale_percentage,
    pre_sale_prices,
  }: RecommendationsServiceProductData,
  currency: CurrencyCode,
): ProductCarouselProduct => ({
  title,
  src: images[0]?.href || '',
  href: links[0]?.href || '',
  alt: '',
  isNew,
  onSale,
  isPurchasable,
  price: prices?.find((prices) => prices.currency === currency),
  preSalePrice: pre_sale_prices?.find((price) => price.currency === currency),
  salePercentage: sale_percentage ?? undefined,
});

export const RecentlyViewed = (props: RecentlyViewedProps) => {
  const {
    currency,
    items = [],
    onNavigateCarousel,
    placementTitle = '',
    strategy = '',
    onVisible,
    onProductClick,
  } = props;
  const products = useRichRelevanceData(items);
  return (
    <div data-testid="RecentlyViewedCarousel">
      <ProductCarousel
        isLoading={products !== null && products.length === 0}
        products={products.map((product) =>
          transformRecommendationsServiceProductDataToProductCarouselProduct(product, currency),
        )}
        title={placementTitle}
        onVisible={() => {
          onVisible?.({ products, strategy });
        }}
        onNavigation={(swiped, control) => {
          onNavigateCarousel?.(swiped, control, { numberOfItems: products.length, strategy });
        }}
        onProductClick={(evt, { index }) => {
          onProductClick?.(evt, {
            product: products[index],
            index,
            numberOfItems: products.length,
            strategy,
          });
        }}
      />
    </div>
  );
};
